import React, { useEffect, useState } from "react";
import { Table, Input, Button, message, Select, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getRequest, postRequest } from "../../../../service/index"; // Adjust path if needed
import RunNewPayRoll from '../../../../modals/runNewPayRoll';
import ModalWrapper from '../../../../Modal';
import { getEmployeeData } from "../../../../utils/localStorage";


function getMonthName(monthNumber) {
  // monthNumber: 1-12
  return new Date(2000, monthNumber - 1, 1).toLocaleString("default", { month: "long" });
}

function mapPayrollSummaryItem(item) {
  const { month, year, totals, parentId, startDate, endDate, status, createdAt } = item;

  let formattedStartDate, formattedEndDate;

  if (startDate && endDate) {
    formattedStartDate = new Date(startDate).toISOString().split("T")[0];
    formattedEndDate = new Date(endDate).toISOString().split("T")[0];
  } else {
    formattedStartDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    formattedEndDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  }

  return {
    key: `${year}-${month}`,
    payPlan: "Monthly Payroll",
    scheduleType: "Salary Based",
    payrollType: "Regular",
    status: status || "pending",
    totalEmployees: totals?.totalEmployees ?? 0,
    payPeriod: `${formattedStartDate} - ${formattedEndDate}`,
    payDate: `01/${String(month + 1 > 12 ? 1 : month + 1).padStart(2, "0")}/${month + 1 > 12 ? year + 1 : year}`,
    totalDeductions: totals?.totalDeductions ?? 0,
    netSalary: totals?.totalNetSalary ?? 0,
    basicSalary: totals?.totalBasicSalary ?? 0,
    overtimeSalary: totals?.totalOvertimeSalary ?? 0,
    totalIncomeTax: totals?.totalIncomeTax ?? 0,
    month,
    year,
    parentId,
    createdAt,
  };
}

const RunPayroll = ({ onRowClick }) => {
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [runPayrollModalVisible, setRunPayrollModalVisible] = useState(false);
  const [combinePayrollModalVisible, setCombinePayrollModalVisible] = useState(false);
  const [combinePayrollLoading, setCombinePayrollLoading] = useState(false);
  const [selectedCombineParentIds, setSelectedCombineParentIds] = useState([]);

  // Permission checks
  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const runPayrollPermissions = employeeData?.designation?.permissions?.Payroll?.['Run Payroll'] || [];
  const showDecimals = employeeData?.decimal !== false;
  const currency = employeeData?.currency || 'RS';
  const formatAmount = (val) => {
    const amount = val !== undefined && val !== null ? Number(val) : 0;
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    });
    return `${currency} ${formatted}`;
  };
  
  const canView = userRole === "companyadmin" || runPayrollPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || runPayrollPermissions.includes("add");


  // Define columns inside component to access handleDeleteClick
  const columns = [
    {
      title: "Pay Plan",
      dataIndex: "payPlan",
      key: "payPlan",
      render: () => "Monthly Payroll",
      sorter: false,
    },
    {
      title: "Schedule Type",
      dataIndex: "scheduleType",
      key: "scheduleType",
      render: () => "Salary Based",
      sorter: false,
    },
    {
      title: "Payroll Type",
      dataIndex: "payrollType",
      key: "payrollType",
      render: () => "Regular",
      sorter: false,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const getStatusColor = (value) => {
          switch (value?.toLowerCase()) {
            case "pending":
              return "orange";
            case "processed":
              return "blue";
            case "approved":
              return "green";
            default:
              return "default";
          }
        };
        return (
          <Tag color={getStatusColor(status)}>
            {status?.charAt(0).toUpperCase() + status?.slice(1) || "Pending"}
          </Tag>
        );
      },
      sorter: false,
    },
    {
      title: "Total Employees",
      dataIndex: "totalEmployees",
      key: "totalEmployees",
      sorter: (a, b) => a.totalEmployees - b.totalEmployees,
    },
    {
      title: "Pay Period",
      dataIndex: "payPeriod",
      key: "payPeriod",
      sorter: (a, b) => {
        const aDate = new Date(a.payPeriod.split(" - ")[0]);
        const bDate = new Date(b.payPeriod.split(" - ")[0]);
        return aDate - bDate;
      },
    },
    {
      title: "Pay Date",
      dataIndex: "payDate",
      key: "payDate",
      sorter: (a, b) => new Date(a.payDate) - new Date(b.payDate),
    },
    {
      title: "Total Deductions",
      dataIndex: "totalDeductions",
      key: "totalDeductions",
      render: (val) => formatAmount(val),
      sorter: (a, b) => a.totalDeductions - b.totalDeductions,
    },
    {
      title: "Net Salary",
      dataIndex: "netSalary",
      key: "netSalary",
      render: (val) => formatAmount(val),
      sorter: (a, b) => a.netSalary - b.netSalary,
    },
    {
      title: "Basic Salary",
      dataIndex: "basicSalary",
      key: "basicSalary",
      render: (val) => formatAmount(val),
      sorter: (a, b) => a.basicSalary - b.basicSalary,
    },
    {
      title: "Overtime Salary",
      dataIndex: "overtimeSalary",
      key: "overtimeSalary",
      render: (val) => formatAmount(val),
      sorter: (a, b) => a.overtimeSalary - b.overtimeSalary,
    },
    {
      title: "Income Tax",
      dataIndex: "totalIncomeTax",
      key: "totalIncomeTax",
      render: (val) => formatAmount(val),
      sorter: (a, b) => a.totalIncomeTax - b.totalIncomeTax,
    },

  ];

  useEffect(() => {
    const fetchPayroll = async () => {
      setLoading(true);
      try {
        if (!canView) {
          setPayrollData([]);
          return;
        }
        const res = await getRequest("/api/monthlysalery");
        if (res?.success && Array.isArray(res.data)) {
          // Map API data to table data
          const mapped = res.data.map(mapPayrollSummaryItem);
          setPayrollData(mapped);
        } else {
          setPayrollData([]);
        }
      } catch (err) {
        setPayrollData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPayroll();
  }, [canView]);

  // Function to handle Run New Payroll
  const handleRunNewPayroll = () => {
    if (!canAdd) {
      message.warning("You don't have permission to run new payroll.");
      return;
    }
    setRunPayrollModalVisible(true);
  };

  // Function to handle modal close
  const handleCloseRunPayrollModal = () => {
    setRunPayrollModalVisible(false);
  };

  const handleOpenCombineModal = () => {
    if (!canAdd) {
      message.warning("You don't have permission to combine payroll.");
      return;
    }
    setSelectedCombineParentIds([]);
    setCombinePayrollModalVisible(true);
  };

  const handleCloseCombineModal = () => {
    if (combinePayrollLoading) return;
    setCombinePayrollModalVisible(false);
    setSelectedCombineParentIds([]);
  };

  const combinePayrollOptions = Array.from(
    new Map(
      (payrollData || [])
        .filter((p) => p?.parentId)
        .map((p) => [
          p.parentId,
          {
            value: p.parentId,
            label: `${p.payPeriod} (${p.payDate})`,
          },
        ])
    ).values()
  );

  const handleCombinePayroll = async () => {
    if (!canAdd) {
      message.warning("You don't have permission to combine payroll.");
      return;
    }
    if (!selectedCombineParentIds || selectedCombineParentIds.length < 2) {
      message.warning("Please select at least 2 payroll dates to combine.");
      return;
    }

    setCombinePayrollLoading(true);
    try {
      const payload = { parentIds: selectedCombineParentIds };
      const response = await postRequest("/api/monthlysalery/combine", payload);

      if (response?.success) {
        message.success(response?.message || "Payrolls combined successfully!");
        setCombinePayrollModalVisible(false);
        setSelectedCombineParentIds([]);
        await handlePayrollRunSuccess();
      } else {
        message.error(response?.message || "Failed to combine payrolls.");
      }
    } catch (err) {
      console.error("Error combining payrolls:", err);
      message.error("Failed to combine payrolls. Please try again.");
    } finally {
      setCombinePayrollLoading(false);
    }
  };

  // Function to handle successful payroll run
  const handlePayrollRunSuccess = async () => {
    // Refresh the payroll data
    const res = await getRequest("/api/monthlysalery");
    if (res?.success && Array.isArray(res.data)) {
      const mapped = res.data.map(mapPayrollSummaryItem);
      setPayrollData(mapped);
    }
  };

  // Handle row click
  const handleRowClick = (record) => {
    if (onRowClick) {
      onRowClick(record);
    }
  };

  // Filter by search (month name or year)
  const filteredData = payrollData.filter((row) => {
    if (!search) return true;
    const monthName = getMonthName(row.month).toLowerCase();
    return (
      monthName.includes(search.toLowerCase()) ||
      String(row.year).includes(search)
    );
  });

  return (
    <>
    <div className="px-3 py-5 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_-4px_6px_rgba(0,0,0,0.05)] rounded-md">
      <div className="flex justify-between mb-4">
        <Input
          placeholder="Search by month or year"
          prefix={<SearchOutlined />}
          style={{ width: 300, height: 40 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2">
          <Button
            className="bg-primary text-white"
            type="primary"
            disabled={!canAdd}
            onClick={() => canAdd ? handleRunNewPayroll() : message.warning("You don't have permission to run new payroll.")}
          >
            Run New Payroll
          </Button>
          <Button
            className="bg-primary text-white"
            type="primary"
            disabled={!canAdd || (payrollData || []).length < 2}
            onClick={() => handleOpenCombineModal()}
          >
            Combine Salary
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={canView ? filteredData : []}
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{
          total: canView ? filteredData.length : 0,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        rowKey="key"
        className="bg-white rounded-lg border border-gray-200"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
      />
      {!canView && !loading && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view payroll data.</div>
      )}
    </div>
     <ModalWrapper
       open={runPayrollModalVisible}
       onRequestClose={handleCloseRunPayrollModal}
       title="Run New Payroll"
     >
       <RunNewPayRoll 
         onClose={handleCloseRunPayrollModal} 
         onSuccess={handlePayrollRunSuccess}
       />
     </ModalWrapper>

      <ModalWrapper
        open={combinePayrollModalVisible}
        onRequestClose={handleCloseCombineModal}
        title="Combine Salary"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-md font-medium">Select Payroll Dates</label>
            <Select
              mode="multiple"
              allowClear
              placeholder={combinePayrollOptions.length ? "Select payroll dates to combine" : "No payrolls available"}
              value={selectedCombineParentIds}
              onChange={(vals) => setSelectedCombineParentIds(vals)}
              options={combinePayrollOptions}
              className="w-full"
              disabled={combinePayrollLoading || !combinePayrollOptions.length}
              maxTagCount="responsive"
              showSearch
              filterOption={(input, option) => {
                const label = option?.label ? String(option.label) : "";
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            />
            <div className="text-sm text-gray-500">
              Select at least 2 payroll dates.
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button onClick={handleCloseCombineModal} disabled={combinePayrollLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCombinePayroll}
              type="primary"
              className="bg-primary text-white"
              loading={combinePayrollLoading}
              disabled={selectedCombineParentIds.length < 2}
            >
              Combine
            </Button>
          </div>
        </div>
      </ModalWrapper>
   </>
  );
};

export default RunPayroll;
